import { Link } from "@tanstack/react-router";
import { Check, ExternalLink } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import type { FieldValues, FormState } from "react-hook-form";

import { Button, buttonVariants } from "~/components/ui/button";
import { cn } from "~/lib/cn";
import { hasError } from "~/lib/form";

const SAVED_CONFIRMATION_MS = 1500;

/** True for a moment after `saving` drops back to false. */
function useJustFinished(saving: boolean): boolean {
  const [justFinished, setJustFinished] = useState(false);
  const wasSaving = useRef(saving);

  useEffect(() => {
    const finished = wasSaving.current && !saving;
    wasSaving.current = saving;
    if (!finished) return;
    setJustFinished(true);
    const timer = setTimeout(
      () => setJustFinished(false),
      SAVED_CONFIRMATION_MS,
    );
    return () => clearTimeout(timer);
  }, [saving]);

  return justFinished;
}

export interface ProfileEditHeaderProps {
  title: string;
  /** Heading level of the title. Use "h2" where the page already has an h1. */
  titleAs?: "h1" | "h2";
  description?: string;
  form?: FormState<FieldValues>;
  /** Override for saving state - use when mutation is managed outside react-hook-form */
  isSaving?: boolean;
  /** Primitive dirty state — used instead of `form` when managing form state outside RHF */
  isDirty?: boolean;
  /** Primitive error state — used instead of `form` when managing form state outside RHF */
  hasErrors?: boolean;
  username: string;
  previewOfferId?: string;
  children?: React.ReactNode;
}

export const ProfileEditHeader = ({
  title,
  titleAs: TitleTag = "h1",
  description,
  form,
  isSaving,
  isDirty,
  hasErrors,
  username,
  previewOfferId,
  children,
}: ProfileEditHeaderProps) => {
  const dirty = form ? form.isDirty : (isDirty ?? false);
  const saving = isSaving ?? form?.isSubmitting ?? false;
  const errored = form ? hasError(form) : (hasErrors ?? false);
  const showSaveButton = form != null || isDirty != null;
  // A failed save leaves the form dirty, so only a clean form reads "Saved".
  const saved = useJustFinished(saving) && !dirty && !errored;
  const linkClassName = cn(buttonVariants(), "shrink-0 gap-2");
  return (
    <div className="sticky top-(--app-topbar-h) z-40 flex h-16 items-center justify-end gap-4 bg-white/50 backdrop-blur-lg">
      <div className="grow">
        <TitleTag className="font-heading text-xl font-medium text-gray-900">
          {title}
        </TitleTag>
        {description && (
          <p className="text-text-secondary text-sm">{description}</p>
        )}
      </div>
      {errored && (
        <div className="text-xs text-red-700">
          Please fix the errors below before saving.
        </div>
      )}
      {previewOfferId ? (
        <Link
          to="/$username/offer/$offerId"
          params={{ username, offerId: previewOfferId }}
          target="_blank"
          className={linkClassName}
        >
          Preview offer <ExternalLink />
        </Link>
      ) : (
        <Link
          to="/$username"
          params={{ username }}
          target="_blank"
          className={linkClassName}
        >
          View profile <ExternalLink />
        </Link>
      )}
      {showSaveButton && (
        <Button
          disabled={!dirty || saving}
          type="submit"
          // Wide enough for "Saving..." and "Saved", so the links beside it stay put.
          className="min-w-24 shrink-0 gap-1.5"
        >
          {saving ? (
            "Saving..."
          ) : saved ? (
            <>
              <Check className="duration-200 animate-in fade-in-0 zoom-in-50" />
              Saved
            </>
          ) : (
            "Save"
          )}
        </Button>
      )}
      <span role="status" className="sr-only">
        {saved ? "Saved" : ""}
      </span>
      {children}
    </div>
  );
};
